import { notifyParent, useConditions } from 'mailup-react-helpers'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import CheckItem, { ICON_BAN, ICON_CHECK } from 'Components/CheckItem/CheckItem'
import {
  Flex,
  FlexItem,
  IconContent1,
  IconPaperClip,
  Link,
  LINK_VARIANT_EXTRALIGHT,
  Panel,
  SPACING_16,
  SpacingVertical,
  Text
} from 'mailup-web-components'
import { IconWrapperSmall } from '../commonComponents/commonComponents'

import { messageState } from 'Pages/MessageCheckupSummary/recoil/state/message'

const BlockingDetails = () => {
  const intl = useIntl()
  const history = useHistory()

  const [isWhiteLabel] = useConditions('co:WHITELABEL')

  const {
    id,
    isBee,
    checkup: {
      blockingCount,
      blockingIssues: {
        LiquidCode,
        Attachments,
        DynamicContents,
        Size,
        Links,
        Content
      },
      wrongAttachments,
      wrongDynamicContents
    }
  } = useRecoilValue(messageState)

  const handleEditLink = () => {
    if (isBee) {
      history.push(`/messages/email/bee/edit/${id}`)
    } else {
      notifyParent('redirectMainFrame', [`form_upd_newsletter.aspx?idNL=${id}`])
    }
  }

  const handleOverviewLink = () => {
    history.push(`/messages/email/${id}/overview`)
  }

  const handleLinksCheckLink = () => {
    const route = `/messages/email/${id}/checkup/linkcheck`
    history.push(route)
  }

  return (
    <Panel>
      {blockingCount === 0 && (
        <CheckItem
          icon={ICON_CHECK}
          title={intl.formatMessage({ id: 'Load.EmailCheckUp.MessageOK' })}
          description={intl.formatMessage({
            id: 'Load.EmailCheckUp.MessageOKDescr'
          })}
          data-mu-test='blocking-details-ok'
        />
      )}

      {LiquidCode && (
        <CheckItem
          icon={ICON_BAN}
          title={intl.formatMessage({
            id: 'Load.EmailCheckUp.CheckLiquidCodeKoTitle'
          })}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          description={intl.formatMessage(
            {
              id: 'Load.EmailCheckUp.CheckLiquidCodeKoDesc'
            },
            {
              link: chunks => (
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleEditLink}
                >
                  {chunks}
                </Link>
              )
            }
          )}
          data-mu-test='blocking-details__liquid-code'
        />
      )}

      {Attachments && (
        <CheckItem
          icon={ICON_BAN}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          title={intl.formatMessage({
            id: 'Load.EmailCheckUp.AttachmentsKoTitle'
          })}
          description={intl.formatMessage(
            {
              id: 'Load.EmailCheckUp.AttachmentsKoDesc'
            },
            {
              link: chunks => (
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleOverviewLink}
                >
                  {chunks}
                </Link>
              )
            }
          )}
          data-mu-test='blocking-details__attachments'
        >
          <SpacingVertical variant={SPACING_16} />
          <Flex margin='0 0 0 32px' flexDirection='column' rowGap='8px'>
            {wrongAttachments.map(attachment => (
              <FlexItem key={attachment}>
                <Text>
                  <IconWrapperSmall color={'var(--flush-mahogany)'}>
                    <IconPaperClip />
                  </IconWrapperSmall>{' '}
                  {attachment}
                </Text>
              </FlexItem>
            ))}
          </Flex>
        </CheckItem>
      )}

      {DynamicContents && (
        <CheckItem
          icon={ICON_BAN}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          title={intl.formatMessage({
            id: 'Load.Common.EmailCheckUp.DynamicContentKoTitle'
          })}
          description={intl.formatMessage(
            {
              id: 'Load.EmailCheckUp.DynamicContentsKoDesc'
            },
            {
              link: chunks => (
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleEditLink}
                >
                  {chunks}
                </Link>
              )
            }
          )}
          data-mu-test='blocking-details__dynamic-contents'
        >
          <SpacingVertical variant={SPACING_16} />
          <Flex margin='0 0 0 32px' flexDirection='column' rowGap='8px'>
            {wrongDynamicContents.map(dc => (
              <FlexItem key={dc}>
                <Text>
                  <IconWrapperSmall color={'var(--flush-mahogany)'}>
                    <IconContent1 />
                  </IconWrapperSmall>{' '}
                  {dc}
                </Text>
              </FlexItem>
            ))}
          </Flex>
        </CheckItem>
      )}

      {Size && (
        <CheckItem
          icon={ICON_BAN}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          title={intl.formatMessage({
            id: 'Load.EmailCheckUp.EmptyMessageKoTitle'
          })}
          description={intl.formatMessage(
            {
              id: 'Load.EmailCheckUp.EmptyMessageKoDesc'
            },
            {
              link: chunks => (
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleEditLink}
                >
                  {chunks}
                </Link>
              )
            }
          )}
          data-mu-test='blocking-details__size'
        />
      )}

      {Links && (
        <CheckItem
          icon={ICON_BAN}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          title={intl.formatMessage({
            id: 'Load.EmailCheckUp.BlackListUrlKoTitle'
          })}
          description={intl.formatMessage(
            {
              id: 'Load.EmailCheckUp.BlackListUrlKoDesc'
            },
            {
              link: chunks => (
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleLinksCheckLink}
                >
                  {chunks}
                </Link>
              )
            }
          )}
          data-mu-test='blocking-details__links'
        />
      )}

      {Content && (
        <CheckItem
          icon={ICON_BAN}
          badge={intl.formatMessage({ id: 'Load.EmailCheckUp.Block' })}
          title={intl.formatMessage({
            id: 'Load.EmailCheckUp.SpamKoTitle'
          })}
          description={intl.formatMessage({
            id: isWhiteLabel
              ? 'Load.EmailCheckUp.SpamKoDescWhitelabel'
              : 'Load.EmailCheckUp.SpamKoDesc'
          })}
          data-mu-test='blocking-details__content'
        />
      )}
    </Panel>
  )
}

export default BlockingDetails
